import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fade
} from '@mui/material';
import {
  ChevronDown,
  Zap,
  Shield,
  Headphones,
  Play,
  ArrowRight,
  BadgeCheck,
  CloudCheck,
  Star,
  Quote,
  Youtube
} from 'lucide-react';
import logoPainel from '../imagens/logopainelftm.png.ico';
import thumbVideo from '../imagens/thumb-videoaula.png';
import trackEvent from '../utils/trackEvent';

// Vídeo aula (YouTube) usado no player e nos botões da landing.
const YT_VIDEO_ID = 'aAJaeVKxCwg';
const YT_WATCH_URL = `https://www.youtube.com/watch?v=${YT_VIDEO_ID}`;

// Estilo reutilizável do botão principal (CTA) com gradiente e brilho.
const ctaSx = {
  px: { xs: 3.5, sm: 4 }, py: { xs: 1.35, sm: 1.5 },
  fontSize: { xs: '0.98rem', sm: '1.05rem' }, fontWeight: 700, borderRadius: 2.5,
  background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
  boxShadow: '0 10px 28px rgba(124,58,237,0.38)',
  transition: 'all 0.25s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)',
    boxShadow: '0 14px 34px rgba(124,58,237,0.5)',
    transform: 'translateY(-2px)',
  },
};

const outlineSx = {
  px: { xs: 3.5, sm: 4 }, py: { xs: 1.35, sm: 1.5 },
  fontSize: { xs: '0.98rem', sm: '1.05rem' }, fontWeight: 700, borderRadius: 2.5,
  borderColor: 'rgba(124, 58, 237, 0.45)', color: '#C4B5FD', borderWidth: 1.5,
  '&:hover': { borderColor: '#7C3AED', borderWidth: 1.5, background: 'rgba(124, 58, 237, 0.1)' },
};

// Botão "Watch" estilizado (glow + reflexo) adaptado à paleta roxa do FTM.
const watchBtnSx = {
  px: { xs: 3, sm: 5 }, py: { xs: 1.35, sm: 1.6 },
  fontSize: { xs: '1rem', sm: '1.2rem' }, fontWeight: 700,
  border: 'none', outline: 'none', borderRadius: '0.4rem',
  cursor: 'pointer', textTransform: 'uppercase',
  background: '#0E0E1A', color: '#EAEAEA',
  transition: '0.6s',
  boxShadow: '0px 0px 60px rgba(124, 58, 237, 0.55)',
  WebkitBoxReflect: 'below 10px linear-gradient(to bottom, rgba(0,0,0,0.0), rgba(0,0,0,0.4))',
  '&:active': { scale: '0.92' },
  '&:hover': {
    background: 'linear-gradient(270deg, rgba(91, 33, 182, 0.68) 0%, rgba(139, 92, 246, 0.87) 60%)',
    color: '#fff',
    boxShadow: '0px 0px 70px rgba(139, 92, 246, 0.7)',
  },
};

const Eyebrow = ({ children }) => (
  <Typography sx={{
    color: '#A78BFA', fontWeight: 800, fontSize: '0.78rem',
    letterSpacing: '0.16em', textTransform: 'uppercase', mb: 1.5,
  }}>
    {children}
  </Typography>
);

const LandingPage = () => {
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);

  useEffect(() => {
    trackEvent('ViewContent', { contentName: 'Landing Page' });
  }, []);

  const steps = [
    { number: '01', title: 'Acesse o Painel FTM', description: 'Crie sua conta e faça login na plataforma.', icon: <Zap size={26} strokeWidth={1.75} /> },
    { number: '02', title: 'Preencha as Informações', description: 'Insira os dados necessários para gerar seu site.', icon: <CloudCheck size={26} strokeWidth={1.75} /> },
    { number: '03', title: 'Envie para a Meta', description: 'O site é gerado automaticamente com nosso template.', icon: <ArrowRight size={26} strokeWidth={1.75} /> },
    { number: '04', title: 'BM Verificada!', description: 'Em 1 a 3 horas sua BM estará verificada.', icon: <BadgeCheck size={26} strokeWidth={1.75} /> },
  ];

  const features = [
    { icon: <Zap size={30} strokeWidth={1.75} />, title: 'Rápido', description: 'Verificação em menos de 3 horas. Sem burocracia.' },
    { icon: <Shield size={30} strokeWidth={1.75} />, title: 'Sem CNPJ', description: 'Não precisa de CNPJ no seu nome, domínio ou hospedagem.' },
    { icon: <Headphones size={30} strokeWidth={1.75} />, title: 'Suporte Total', description: 'Vídeo aula completa e suporte dedicado via WhatsApp.' },
  ];

  const testimonials = [
    { text: 'Consegui verificar minha BM em 3h, muito obrigado!', name: 'Lucas Andrade', role: 'Gestor de Tráfego', initials: 'LA', grad: 'linear-gradient(135deg,#7C3AED,#4F46E5)' },
    { text: 'Finalmente uma vídeo aula que realmente funciona!', name: 'Rafaela Lima', role: 'Afiliada', initials: 'RL', grad: 'linear-gradient(135deg,#10B981,#059669)' },
    { text: 'Tava precisando liberar a API oficial do WhatsApp, mas precisava verificar minha BM. Fiz o passo a passo e deu certo.', name: 'Bruno Carvalho', role: 'Dono de Agência', initials: 'BC', grad: 'linear-gradient(135deg,#F59E0B,#D97706)' },
    { text: 'Fiz certinho e verificou rapidinho, muito obrigado!', name: 'Camila Souza', role: 'Infoprodutora', initials: 'CS', grad: 'linear-gradient(135deg,#EC4899,#DB2777)' },
  ];

  const faqs = [
    { question: 'Como funciona o Painel FTM?', answer: 'O Painel FTM cria um site profissional com nosso template exclusivo que já aprovou mais de 10.000 BM\'s. Você preenche as informações, o site é gerado e hospedado automaticamente, e você usa ele para verificar sua BM na Meta.' },
    { question: 'Terei suporte para utilizar?', answer: 'Sim! Oferecemos vídeo aula completa ensinando passo a passo, além de suporte via WhatsApp para tirar dúvidas.' },
    { question: 'Todas as minhas BM\'s vão verificar mesmo?', answer: 'Nosso template já aprovou mais de 10.000 BM\'s. A taxa de aprovação é altíssima quando as instruções são seguidas corretamente.' },
    { question: 'Para que serve a verificação de BM?', answer: 'A verificação do Portfólio Empresarial (Business Manager) na Meta permite que você gerencie anúncios com mais confiança, acesse recursos avançados e tenha maior credibilidade na plataforma.' },
    { question: 'Os sites não ficam marcados?', answer: 'Não. Os sites são gerados com domínios únicos e hospedados em infraestrutura limpa, sem histórico de marcação.' },
  ];

  return (
    <Box sx={{ background: '#0B0F1A', minHeight: '100vh', overflowX: 'hidden' }}>

      {/* Navbar */}
      <Box sx={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
        background: 'rgba(11, 15, 26, 0.8)', backdropFilter: 'blur(14px)',
        borderBottom: '1px solid rgba(124, 58, 237, 0.15)',
      }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: { xs: 1.1, sm: 1.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, cursor: 'pointer' }} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <img src={logoPainel} alt="FTM" style={{ width: 34, height: 34, borderRadius: 8 }} />
              <Typography sx={{ fontWeight: 800, fontSize: { xs: '1rem', sm: '1.1rem' }, color: '#fff' }}>
                PAINEL <span style={{ color: '#A78BFA' }}>FTM</span>
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1.5 } }}>
              <Button
                variant="text"
                onClick={() => navigate('/login')}
                sx={{ color: '#9CA3AF', fontWeight: 600, px: { xs: 1.25, sm: 2 }, minWidth: 0, '&:hover': { color: '#fff', background: 'transparent' } }}
              >
                Entrar
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate('/register')}
                sx={{ ...ctaSx, px: { xs: 2, sm: 3 }, py: { xs: 0.75, sm: 0.9 }, fontSize: { xs: '0.85rem', sm: '0.9rem' }, boxShadow: '0 6px 18px rgba(124,58,237,0.32)' }}
              >
                Criar Conta
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Hero */}
      <Fade in timeout={600}>
        <Box sx={{ pt: { xs: 13, sm: 18 }, pb: { xs: 7, sm: 12 }, position: 'relative' }}>
          {/* glow blobs */}
          <Box sx={{
            position: 'absolute', top: '-5%', left: '50%', transform: 'translateX(-50%)',
            width: { xs: 420, sm: 680 }, height: { xs: 420, sm: 680 }, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(124,58,237,0.22) 0%, transparent 65%)',
            pointerEvents: 'none', filter: 'blur(6px)',
          }} />
          <Box sx={{
            position: 'absolute', top: '20%', right: '-10%',
            width: 320, height: 320, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(16,185,129,0.1) 0%, transparent 70%)',
            pointerEvents: 'none', display: { xs: 'none', md: 'block' },
          }} />
          {/* subtle grid */}
          <Box sx={{
            position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.5,
            backgroundImage: 'linear-gradient(rgba(124,58,237,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(124,58,237,0.05) 1px, transparent 1px)',
            backgroundSize: '44px 44px',
            maskImage: 'radial-gradient(circle at 50% 30%, #000 0%, transparent 70%)',
            WebkitMaskImage: 'radial-gradient(circle at 50% 30%, #000 0%, transparent 70%)',
          }} />

          <Container maxWidth="md" sx={{ position: 'relative', textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <Box component="span" sx={{
                display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 0.7, borderRadius: 50,
                background: 'rgba(124, 58, 237, 0.12)', border: '1px solid rgba(124, 58, 237, 0.3)',
                color: '#C4B5FD', fontSize: { xs: '0.78rem', sm: '0.85rem' }, fontWeight: 600,
              }}>
                <Box sx={{
                  width: 8, height: 8, borderRadius: '50%', background: '#10B981',
                  boxShadow: '0 0 0 0 rgba(16,185,129,0.6)',
                  animation: 'pulseDot 2s infinite',
                  '@keyframes pulseDot': {
                    '0%': { boxShadow: '0 0 0 0 rgba(16,185,129,0.5)' },
                    '70%': { boxShadow: '0 0 0 8px rgba(16,185,129,0)' },
                    '100%': { boxShadow: '0 0 0 0 rgba(16,185,129,0)' },
                  },
                }} />
                +10.000 BM's verificadas
              </Box>
            </Box>
            <Typography variant="h1" sx={{
              fontWeight: 900, color: '#fff', mb: 2.5, lineHeight: 1.08, letterSpacing: '-0.02em',
              fontSize: { xs: '2.1rem', sm: '2.9rem', md: '3.6rem' },
            }}>
              Verifique sua BM na Meta{' '}
              <Box component="span" sx={{
                background: 'linear-gradient(120deg, #A78BFA 0%, #7C3AED 60%, #6D28D9 100%)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
              }}>
                em menos de 3 horas
              </Box>
            </Typography>
            <Typography sx={{
              color: '#9CA3AF', mb: 4, maxWidth: 600, mx: 'auto', lineHeight: 1.7,
              fontSize: { xs: '0.98rem', sm: '1.15rem' },
            }}>
              Cansado de portfólios em análise durante semanas? Com o Painel FTM, suas BM's verificam sem precisar de domínio, hospedagem ou CNPJ.
            </Typography>
            <Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 2 }, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained" size="large" fullWidth={false}
                onClick={() => navigate('/register')}
                endIcon={<ArrowRight size={20} strokeWidth={2} />}
                sx={{ ...ctaSx, width: { xs: '100%', sm: 'auto' } }}
              >
                Começar Agora
              </Button>
              <Button
                variant="contained" size="large" disableElevation
                startIcon={<Play size={20} strokeWidth={2} />}
                href={YT_WATCH_URL}
                target="_blank"
                sx={{ ...watchBtnSx, width: { xs: '100%', sm: 'auto' } }}
              >
                Assistir Vídeo Aula
              </Button>
            </Box>
          </Container>
        </Box>
      </Fade>

      {/* Video CTA */}
      <Box sx={{ pt: { xs: 2, sm: 3 }, pb: { xs: 7, sm: 10 } }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Eyebrow>Demonstração</Eyebrow>
          <Typography variant="h4" sx={{ color: '#fff', mb: 1.5 }}>
            Veja como é fácil
          </Typography>
          <Typography sx={{ color: '#9CA3AF', mb: { xs: 3.5, md: 5 }, fontSize: { xs: '0.95rem', sm: '1rem' } }}>
            Assista nossa vídeo aula completa e aprenda a verificar sua BM em minutos.
          </Typography>

          {/* Player card: clique para dar play direto na página */}
          <Box sx={{
            position: 'relative', mx: 'auto', mb: { xs: 2.5, md: 3.5 },
            width: '100%', maxWidth: { xs: '100%', sm: 620, md: 880 },
            p: { xs: '3px', md: '4px' }, borderRadius: { xs: 3, md: 4 },
            background: 'linear-gradient(135deg, rgba(167,139,250,0.55), rgba(124,58,237,0.15) 45%, rgba(109,40,217,0.55))',
            boxShadow: { xs: '0 12px 40px rgba(124,58,237,0.28)', md: '0 30px 90px rgba(124,58,237,0.32)' },
          }}>
            <Box sx={{
              position: 'relative', width: '100%', aspectRatio: '16 / 9',
              borderRadius: { xs: 2.5, md: 3.5 }, overflow: 'hidden',
              background: '#05060B',
            }}>
              {videoPlaying ? (
                <Box
                  component="iframe"
                  src={`https://www.youtube.com/embed/${YT_VIDEO_ID}?autoplay=1&rel=0&modestbranding=1`}
                  title="Vídeo Aula - Verifique sua BM"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0, display: 'block' }}
                />
              ) : (
                <Box
                  role="button"
                  tabIndex={0}
                  aria-label="Reproduzir vídeo aula"
                  onClick={() => setVideoPlaying(true)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setVideoPlaying(true); }}
                  sx={{
                    position: 'absolute', inset: 0, cursor: 'pointer',
                    '&:hover img': { transform: 'scale(1.04)' },
                    '&:hover .play-core': { transform: 'translate(-50%,-50%) scale(1.08)', background: 'linear-gradient(135deg,#8B5CF6,#6D28D9)' },
                  }}
                >
                  <Box
                    component="img"
                    src={thumbVideo}
                    alt="Vídeo Aula - Verifique sua BM"
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.6s cubic-bezier(0.2,0.8,0.2,1)' }}
                  />
                  {/* leve escurecida para dar contraste ao play */}
                  <Box sx={{
                    position: 'absolute', inset: 0,
                    background: 'linear-gradient(180deg, rgba(5,6,11,0) 40%, rgba(5,6,11,0.45) 100%)',
                  }} />
                  {/* Botão play com anel pulsante */}
                  <Box sx={{
                    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
                    width: { xs: 68, md: 92 }, height: { xs: 68, md: 92 },
                    '@keyframes ftmPulse': {
                      '0%': { transform: 'scale(0.9)', opacity: 0.55 },
                      '70%': { transform: 'scale(1.6)', opacity: 0 },
                      '100%': { transform: 'scale(1.6)', opacity: 0 },
                    },
                    '&::before, &::after': {
                      content: '""', position: 'absolute', inset: 0, borderRadius: '50%',
                      border: '2px solid rgba(167,139,250,0.7)', animation: 'ftmPulse 2.4s ease-out infinite',
                    },
                    '&::after': { animationDelay: '1.2s' },
                  }}>
                    <Box className="play-core" sx={{
                      position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
                      width: { xs: 68, md: 92 }, height: { xs: 68, md: 92 }, borderRadius: '50%',
                      background: 'linear-gradient(135deg, rgba(124,58,237,0.92), rgba(91,33,182,0.92))',
                      backdropFilter: 'blur(4px)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 10px 30px rgba(124,58,237,0.55), inset 0 1px 1px rgba(255,255,255,0.35)',
                      transition: 'all 0.3s ease',
                    }}>
                      <Play size={34} strokeWidth={2} color="#fff" fill="#fff" style={{ marginLeft: 4 }} />
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          <Typography sx={{ color: '#8B93A7', mb: 2.5, fontSize: { xs: '0.82rem', sm: '0.88rem' } }}>
            {videoPlaying
              ? 'Prefere ver no YouTube? Lá você acessa todos os links da descrição.'
              : 'Toque para dar play. Assista no YouTube para acessar os links da descrição.'}
          </Typography>

          <Button
            variant="outlined" size="large"
            startIcon={<Youtube size={22} strokeWidth={2} />}
            href={YT_WATCH_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent('Lead', { contentName: 'Assistir no YouTube' })}
            sx={{
              ...outlineSx, width: { xs: '100%', sm: 'auto' },
              borderColor: 'rgba(255,255,255,0.18)', color: '#fff',
              '&:hover': { borderColor: '#7C3AED', background: 'rgba(124,58,237,0.12)', color: '#fff' },
            }}
          >
            Assistir no YouTube
          </Button>
        </Container>
      </Box>

      {/* How it works */}
      <Fade in timeout={800}>
        <Box sx={{ py: { xs: 7, sm: 10 }, background: 'rgba(124, 58, 237, 0.035)', borderTop: '1px solid rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 6 } }}>
              <Eyebrow>Passo a passo</Eyebrow>
              <Typography variant="h4" sx={{ color: '#fff', mb: 1 }}>
                Como Funciona
              </Typography>
              <Typography sx={{ color: '#9CA3AF', maxWidth: 500, mx: 'auto' }}>
                Em 4 passos simples, sua BM estará verificada.
              </Typography>
            </Box>
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {steps.map((step, i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <Paper sx={{
                    p: { xs: 2.5, sm: 3 }, height: '100%', textAlign: 'center', position: 'relative',
                    background: 'linear-gradient(160deg, #161C2B 0%, #11151F 100%)',
                    border: '1px solid rgba(255,255,255,0.06)',
                    transition: 'all 0.3s ease',
                    '&:hover': { borderColor: 'rgba(124, 58, 237, 0.45)', transform: 'translateY(-4px)', boxShadow: '0 12px 36px rgba(124, 58, 237, 0.18)' },
                  }}>
                    <Box sx={{
                      width: 56, height: 56, borderRadius: 2.5, mx: 'auto', mb: 2,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.25), rgba(124, 58, 237, 0.06))',
                      border: '1px solid rgba(124,58,237,0.2)',
                      color: '#C4B5FD',
                    }}>
                      {step.icon}
                    </Box>
                    <Typography sx={{ color: '#7C3AED', fontWeight: 800, fontSize: '0.72rem', letterSpacing: '0.12em', mb: 1 }}>
                      PASSO {step.number}
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#fff', mb: 1, fontSize: '1.02rem' }}>
                      {step.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#9CA3AF' }}>
                      {step.description}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      </Fade>

      {/* Features */}
      <Box sx={{ py: { xs: 7, sm: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
            <Grid item xs={12} md={5}>
              <Eyebrow>Vantagens</Eyebrow>
              <Typography variant="h4" sx={{ color: '#fff', mb: 2 }}>
                Por que usar o{' '}
                <Box component="span" sx={{ color: '#A78BFA' }}>Painel FTM</Box>?
              </Typography>
              <Typography sx={{ color: '#9CA3AF', mb: 3, lineHeight: 1.8, fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                O Painel FTM cria um site profissional com nosso template exclusivo que já aprovou mais de 10.000 BM's. Sem a necessidade de ter um CNPJ, domínio ou hospedagem.
              </Typography>
              <Button
                variant="contained" size="large"
                onClick={() => navigate('/register')}
                sx={{ ...ctaSx, width: { xs: '100%', sm: 'auto' } }}
              >
                Criar Conta Grátis
              </Button>
            </Grid>
            <Grid item xs={12} md={7}>
              <Grid container spacing={2}>
                {features.map((feat, i) => (
                  <Grid item xs={12} key={i}>
                    <Paper sx={{
                      p: { xs: 2.5, sm: 3 }, display: 'flex', alignItems: 'center', gap: { xs: 2, sm: 3 },
                      background: 'linear-gradient(160deg, #161C2B 0%, #11151F 100%)',
                      border: '1px solid rgba(255,255,255,0.06)',
                      transition: 'all 0.3s ease',
                      '&:hover': { borderColor: 'rgba(124, 58, 237, 0.4)', transform: 'translateX(4px)' },
                    }}>
                      <Box sx={{
                        width: { xs: 54, sm: 64 }, height: { xs: 54, sm: 64 }, borderRadius: 2.5, flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.25), rgba(124, 58, 237, 0.06))',
                        border: '1px solid rgba(124,58,237,0.2)',
                        color: '#C4B5FD',
                      }}>
                        {feat.icon}
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ color: '#fff', mb: 0.5, fontSize: '1.05rem' }}>{feat.title}</Typography>
                        <Typography variant="body2" sx={{ color: '#9CA3AF' }}>{feat.description}</Typography>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats */}
      <Box sx={{ py: { xs: 5, sm: 7 } }}>
        <Container maxWidth="md">
          <Paper sx={{
            p: { xs: 3, sm: 5 }, borderRadius: 4,
            background: 'linear-gradient(135deg, rgba(124,58,237,0.14) 0%, rgba(124,58,237,0.04) 100%)',
            border: '1px solid rgba(124,58,237,0.22)',
          }}>
            <Grid container spacing={{ xs: 3, sm: 2 }} justifyContent="center">
              {[
                { value: '10K+', label: "BM's Verificadas" },
                { value: '<3h', label: 'Tempo Médio' },
                { value: '99%', label: 'Taxa de Aprovação' },
              ].map((stat, i) => (
                <Grid item xs={12} sm={4} key={i} sx={{
                  textAlign: 'center',
                  borderLeft: { sm: i === 0 ? 'none' : '1px solid rgba(255,255,255,0.08)' },
                }}>
                  <Typography sx={{
                    fontSize: { xs: '2.4rem', sm: '3rem' }, fontWeight: 900, lineHeight: 1,
                    background: 'linear-gradient(120deg, #C4B5FD, #7C3AED)',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                  }}>
                    {stat.value}
                  </Typography>
                  <Typography sx={{ color: '#9CA3AF', mt: 1, fontWeight: 500 }}>{stat.label}</Typography>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Container>
      </Box>

      {/* Depoimentos */}
      <Box sx={{ py: { xs: 7, sm: 10 }, background: 'rgba(16, 185, 129, 0.03)', borderTop: '1px solid rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 6 } }}>
            <Eyebrow>Depoimentos</Eyebrow>
            <Typography variant="h4" sx={{ color: '#fff', mb: 1 }}>
              Quem usa, aprova
            </Typography>
            <Typography sx={{ color: '#9CA3AF', maxWidth: 520, mx: 'auto' }}>
              Veja o que nossos clientes falam sobre a verificação de BM no Painel FTM.
            </Typography>
          </Box>
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {testimonials.map((t, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Paper sx={{
                  p: { xs: 2.5, sm: 3 }, height: '100%', position: 'relative', overflow: 'hidden',
                  display: 'flex', flexDirection: 'column',
                  background: 'linear-gradient(160deg, #161C2B 0%, #11151F 100%)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  transition: 'all 0.3s ease',
                  '&:hover': { borderColor: 'rgba(16,185,129,0.4)', transform: 'translateY(-4px)', boxShadow: '0 12px 36px rgba(16,185,129,0.12)' },
                }}>
                  <Quote size={44} strokeWidth={1.5} color="#10B981" style={{ position: 'absolute', top: 12, right: 12, opacity: 0.16 }} />
                  <Box sx={{ display: 'flex', gap: 0.3, mb: 1.5 }}>
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} size={16} strokeWidth={1.5} color="#10B981" fill="#10B981" />
                    ))}
                  </Box>
                  <Typography sx={{ color: '#D1D5DB', lineHeight: 1.6, fontSize: '0.92rem', mb: 3, flex: 1 }}>
                    "{t.text}"
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ position: 'relative', flexShrink: 0 }}>
                      <Box sx={{
                        width: 42, height: 42, borderRadius: '50%', background: t.grad,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontWeight: 700, fontSize: '0.9rem',
                      }}>
                        {t.initials}
                      </Box>
                      <Box sx={{
                        position: 'absolute', bottom: 0, right: 0, width: 11, height: 11, borderRadius: '50%',
                        background: '#10B981', border: '2px solid #11151F',
                      }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.2 }}>{t.name}</Typography>
                      <Typography sx={{ color: '#10B981', fontSize: '0.78rem' }}>{t.role}</Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FAQ */}
      <Box sx={{ py: { xs: 7, sm: 10 } }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 5 } }}>
            <Eyebrow>Dúvidas</Eyebrow>
            <Typography variant="h4" sx={{ color: '#fff', mb: 1 }}>
              Perguntas Frequentes
            </Typography>
            <Typography sx={{ color: '#9CA3AF' }}>
              Tire suas dúvidas sobre o Painel FTM.
            </Typography>
          </Box>
          {faqs.map((faq, i) => (
            <Accordion
              key={i}
              expanded={expandedFaq === i}
              onChange={() => setExpandedFaq(expandedFaq === i ? false : i)}
              sx={{
                background: '#141926',
                border: '1px solid rgba(255,255,255,0.06)',
                mb: 1.5, borderRadius: '12px !important',
                boxShadow: 'none',
                '&:before': { display: 'none' },
                '&.Mui-expanded': { borderColor: 'rgba(124, 58, 237, 0.35)', background: 'rgba(124,58,237,0.05)' },
              }}
            >
              <AccordionSummary expandIcon={<ChevronDown size={20} strokeWidth={2} color="#A78BFA" />} sx={{ px: { xs: 2, sm: 3 } }}>
                <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: { xs: '0.92rem', sm: '1rem' } }}>{faq.question}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 2, sm: 3 } }}>
                <Typography sx={{ color: '#9CA3AF', lineHeight: 1.7, fontSize: { xs: '0.9rem', sm: '0.95rem' } }}>{faq.answer}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>

      {/* Final CTA */}
      <Box sx={{ py: { xs: 7, sm: 12 } }}>
        <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
          <Paper sx={{
            p: { xs: 3.5, sm: 6 }, borderRadius: 4, position: 'relative', overflow: 'hidden',
            background: 'linear-gradient(160deg, #161C2B 0%, #11151F 100%)',
            border: '1px solid rgba(124,58,237,0.22)',
          }}>
            <Box sx={{
              position: 'absolute', top: '-40%', left: '50%', transform: 'translateX(-50%)',
              width: 400, height: 400, borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(124,58,237,0.18) 0%, transparent 70%)', pointerEvents: 'none',
            }} />
            <Box sx={{ position: 'relative' }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.4, mb: 2 }}>
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={20} strokeWidth={1.5} color="#FBBF24" fill="#FBBF24" />
                ))}
              </Box>
              <Typography variant="h4" sx={{ color: '#fff', mb: 2 }}>
                Faça o teste agora
              </Typography>
              <Typography sx={{ color: '#9CA3AF', mb: 4, lineHeight: 1.7, fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                Crie sua conta, gere seu site e verifique sua BM em menos de 3 horas.
              </Typography>
              <Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 2 }, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained" size="large"
                  onClick={() => navigate('/register')}
                  endIcon={<ArrowRight size={20} strokeWidth={2} />}
                  sx={{ ...ctaSx, width: { xs: '100%', sm: 'auto' } }}
                >
                  Criar Conta Grátis
                </Button>
                <Button
                  variant="outlined" size="large"
                  onClick={() => navigate('/login')}
                  sx={{ ...outlineSx, width: { xs: '100%', sm: 'auto' } }}
                >
                  Já tenho conta
                </Button>
              </Box>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 4, borderTop: '1px solid rgba(124, 58, 237, 0.12)' }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <img src={logoPainel} alt="FTM" style={{ width: 28, height: 28, borderRadius: 6 }} />
              <Typography sx={{ color: '#6B7280', fontSize: '0.85rem' }}>
                Painel FTM - Verificador de BM's
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2.5 }, flexWrap: 'wrap' }}>
              <Button
                variant="text"
                onClick={() => navigate('/termos-de-uso')}
                sx={{ color: '#9CA3AF', fontSize: '0.8rem', fontWeight: 500, p: 0, minWidth: 0, '&:hover': { color: '#A78BFA', background: 'transparent' } }}
              >
                Termos de Uso
              </Button>
              <Button
                variant="text"
                onClick={() => navigate('/politica-de-privacidade')}
                sx={{ color: '#9CA3AF', fontSize: '0.8rem', fontWeight: 500, p: 0, minWidth: 0, '&:hover': { color: '#A78BFA', background: 'transparent' } }}
              >
                Política de Privacidade
              </Button>
            </Box>
            <Typography sx={{ color: '#4B5563', fontSize: '0.8rem', width: { xs: '100%', sm: 'auto' }, textAlign: { xs: 'center', sm: 'right' } }}>
              &copy; {new Date().getFullYear()} Painel FTM. Todos os direitos reservados.
            </Typography>
          </Box>
        </Container>
      </Box>

    </Box>
  );
};

export default LandingPage;
